import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'wouter';
import { useAuth } from '@/hooks/useAuth';
import { DeveloperNav } from '@/components/DeveloperNav';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Code, 
  CheckCircle, 
  ArrowRight,
  Zap,
  Shield,
  Globe,
  Users,
  BarChart3
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface ApiPlan {
  id: number;
  name: string;
  description: string;
  tier: string;
  monthlyLimit: number;
  price: number;
  features: string[];
}

interface DeveloperAccount {
  id: number;
  status: string;
}

export default function DeveloperSignup() {
  const { user, isAuthenticated } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    companyName: '',
    website: '',
    description: '',
    useCase: '',
  });
  const [apiPlans, setApiPlans] = useState<ApiPlan[]>([]);
  const [accountLoading, setAccountLoading] = useState(true);
  const [developerAccount, setDeveloperAccount] = useState<DeveloperAccount | null>(null);
  const hasFetchedAccount = useRef(false);

  useEffect(() => {
    if (isAuthenticated && !hasFetchedAccount.current) {
      hasFetchedAccount.current = true;
      fetch('/api/developer/account', { credentials: 'include' })
        .then(res => {
          if (res.ok) return res.json();
          return null;
        })
        .then(data => {
          if (data?.data) {
            setDeveloperAccount(data.data);
            setLocation('/developer/dashboard');
          }
        })
        .catch(() => {})
        .finally(() => setAccountLoading(false));
    } else if (!isAuthenticated) {
      setAccountLoading(false);
    }
  }, [isAuthenticated, setLocation]);

  useEffect(() => {
    loadApiPlans();
  }, []);

  const loadApiPlans = async () => {
    try {
      const response = await fetch('/api/developer/plans');
      if (response.ok) {
        const data = await response.json();
        setApiPlans(data.data);
      }
    } catch (error) {
      console.error('Error loading API plans:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      toast({
        title: 'Authentication Required',
        description: 'Please log in to create a developer account',
        variant: 'destructive',
      });
      return;
    }

    try {
      setLoading(true);
      const response = await fetch('/api/developer/account', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        const data = await response.json();
        toast({
          title: 'Application Submitted',
          description: 'Your developer account is now pending review. We\'ll notify you within 24 hours.',
        });
        // Redirect to developer dashboard
        setLocation('/developer/dashboard');
      } else {
        const error = await response.json();
        toast({
          title: 'Error',
          description: error.message || 'Failed to create developer account',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error creating developer account:', error);
      toast({
        title: 'Error',
        description: 'Failed to create developer account',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-gray-900">Developer Signup</CardTitle>
            <CardDescription>
              Please log in to create a developer account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild className="w-full">
              <a href="/auth">Log In</a>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (accountLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (developerAccount) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Redirecting to your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <DeveloperNav />
      {/* Hero Section */}
      <div className="py-12 sm:py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            Build Amazing Apps with
            <span className="text-blue-600"> AI Story Generation</span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
            Access our powerful API to create personalized bedtime stories, 
            educational content, and interactive experiences for children.
          </p>
          <div className="flex flex-wrap justify-center gap-3 sm:gap-4 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              Easy Integration
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              RESTful API
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              Real-time Generation
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-500" />
              Comprehensive Documentation
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-12 sm:py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-gray-900 mb-8 sm:mb-12">
            Why Choose Our API?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            <div className="text-center p-4">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 touch-manipulation">
                <Zap className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-2">Lightning Fast</h3>
              <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
                Generate stories in seconds with our optimized AI models
              </p>
            </div>
            
            <div className="text-center p-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 touch-manipulation">
                <Shield className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-2">Secure & Reliable</h3>
              <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
                Enterprise-grade security with 99.9% uptime guarantee
              </p>
            </div>
            
            <div className="text-center p-4">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4 touch-manipulation">
                <Globe className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-2">Global Scale</h3>
              <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
                Serve users worldwide with our distributed infrastructure
              </p>
            </div>
            
            <div className="text-center p-4">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4 touch-manipulation">
                <Users className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-2">Developer Friendly</h3>
              <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
                Comprehensive docs, SDKs, and dedicated support
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* API Plans */}
      <div className="py-12 sm:py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-gray-900 mb-8 sm:mb-12">
            Choose Your Plan
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {apiPlans.map((plan) => (
              <Card key={plan.id} className={`relative ${plan.tier === 'pro' ? 'border-blue-500 shadow-lg' : ''}`}>
                {plan.tier === 'pro' && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                      Most Popular
                    </span>
                  </div>
                )}
                <CardHeader className="text-center">
                  <CardTitle className="text-2xl">{plan.name}</CardTitle>
                  <div className="text-4xl font-bold text-gray-900 mt-4">
                    ${plan.price}
                    <span className="text-lg font-normal text-gray-600">/month</span>
                  </div>
                  <CardDescription className="mt-2">
                    {plan.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    <li className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                      <span>{plan.monthlyLimit.toLocaleString()} API calls/month</span>
                    </li>
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-center">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Signup Form */}
      <div className="py-12 sm:py-16 px-4">
        <div className="max-w-2xl mx-auto">
          <Card>
            <CardHeader className="text-center">
              <CardTitle className="text-2xl sm:text-3xl">Create Developer Account</CardTitle>
              <CardDescription className="text-base sm:text-lg leading-relaxed">
                Get started with our API in minutes
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="companyName" className="text-sm sm:text-base">Company Name (Optional)</Label>
                    <Input
                      id="companyName"
                      value={formData.companyName}
                      onChange={(e) => handleInputChange('companyName', e.target.value)}
                      placeholder="Your company name"
                      className="h-12 text-base"
                      data-testid="input-company-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="website" className="text-sm sm:text-base">Website (Optional)</Label>
                    <Input
                      id="website"
                      type="url"
                      value={formData.website}
                      onChange={(e) => handleInputChange('website', e.target.value)}
                      placeholder="https://yourcompany.com"
                      className="h-12 text-base"
                      data-testid="input-website"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description" className="text-sm sm:text-base">Project Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Tell us about your project and how you plan to use our API..."
                    rows={4}
                    className="min-h-[100px] text-base resize-none"
                    required
                    data-testid="textarea-description"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="useCase" className="text-sm sm:text-base">Use Case</Label>
                  <Textarea
                    id="useCase"
                    value={formData.useCase}
                    onChange={(e) => handleInputChange('useCase', e.target.value)}
                    placeholder="Describe your specific use case and target audience..."
                    rows={4}
                    className="min-h-[100px] text-base resize-none"
                    required
                    data-testid="textarea-use-case"
                  />
                </div>

                <Alert>
                  <BarChart3 className="h-4 w-4" />
                  <AlertDescription>
                    Your account will be reviewed and approved within 24 hours. 
                    You'll receive an email notification once approved.
                  </AlertDescription>
                </Alert>

                <Button 
                  type="submit" 
                  className="w-full h-12 text-base font-medium" 
                  size="lg"
                  disabled={loading}
                  data-testid="button-create-account"
                >
                  {loading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creating Account...
                    </div>
                  ) : (
                    <div className="flex items-center">
                      Create Developer Account
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </div>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-12 sm:py-16 px-4 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4">
            Ready to Build Something Amazing?
          </h2>
          <p className="text-lg sm:text-xl text-gray-300 mb-8 leading-relaxed">
            Join thousands of developers creating innovative applications with our API
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button asChild size="lg" variant="secondary" className="h-12 text-base font-medium" data-testid="button-view-docs">
              <a href="/developer-portal">
                <Code className="w-4 h-4 mr-2" />
                View Documentation
              </a>
            </Button>
            <Button asChild size="lg" className="h-12 text-base font-medium" data-testid="button-contact-sales">
              <a href="mailto:developers@snugglecrafters.com">
                Contact Sales
              </a>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}